import type { LucideIcon } from 'lucide-react';
import {
  Bot, Boxes, Building2, Coins, LogIn, Mail, Megaphone, MessagesSquare, Plug, Search, Share2, Sparkles, UserRoundPlus, Wand2, Eye,
} from 'lucide-react';

import type { FeatureKey, Metrics } from '../../../common/models/feature-usage.model';

/** Each feature as the page shows it: its glyph and the metrics it reads, the first one being the headline. */
export interface FeatureMeta {
  key: FeatureKey;
  icon: LucideIcon;
  metrics: string[];
}

export const FEATURE_META: FeatureMeta[] = [
  { key: 'logins', icon: LogIn, metrics: ['logins', 'activeUsers', 'pageViews'] },
  { key: 'ai', icon: Sparkles, metrics: ['aiCostUsd', 'aiCredits', 'aiTokens', 'aiRequests'] },
  { key: 'credits', icon: Coins, metrics: ['creditsUsed'] },
  { key: 'catalog', icon: Boxes, metrics: ['productsAdded', 'products', 'seamless', 'models3d', 'tilePhotos'] },
  { key: 'rooms', icon: Building2, metrics: ['roomsAdded', 'rooms'] },
  { key: 'visualizer', icon: Eye, metrics: ['visualizerViews', 'visualizerVisitors'] },
  { key: 'shopperChat', icon: MessagesSquare, metrics: ['shopperChats'] },
  { key: 'leads', icon: UserRoundPlus, metrics: ['leadsNew'] },
  { key: 'emails', icon: Mail, metrics: ['emailsSent', 'automationsActive', 'emailsFailed'] },
  { key: 'ads', icon: Megaphone, metrics: ['adCampaigns', 'adCampaignsActive', 'adConnections'] },
  { key: 'contentStudio', icon: Wand2, metrics: ['csGenerations', 'csProjects'] },
  { key: 'social', icon: Share2, metrics: ['socialPublished', 'socialScheduled', 'socialAccounts'] },
  { key: 'assistant', icon: Bot, metrics: ['assistantThreads', 'routinesEnabled', 'channelsConnected'] },
  { key: 'seo', icon: Search, metrics: ['seoReadings', 'analysisRuns', 'reports'] },
  { key: 'integrations', icon: Plug, metrics: ['syncRuns', 'commerceConnected', 'apiIntegration'] },
];

export const metaOf = (key: FeatureKey): FeatureMeta => FEATURE_META.find((f) => f.key === key) || FEATURE_META[0];

/** `contentStudio` → "Content studio": a key spelled out, for the ones without a label. */
export const spellOut = (key: string): string => {
  const words = String(key || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[-_]+/g, ' ').trim().toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : '—';
};

const compact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });
const whole = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

/** Dollars of our cost: cents shown, and fractions of a cent when that is all there is. */
export function formatUsd(value: number | null | undefined): string {
  const v = Number(value) || 0;
  if (v === 0) return '$0';
  if (Math.abs(v) < 0.01) return `$${v.toFixed(4)}`;
  if (Math.abs(v) < 100) return `$${v.toFixed(2)}`;
  return `$${whole.format(Math.round(v))}`;
}

/** A metric's value, as it reads: dollars for costs, compact for token counts, whole numbers otherwise. */
export function formatMetric(key: string, value: number | null | undefined): string {
  const v = Number(value) || 0;
  if (key === 'aiCostUsd') return formatUsd(v);
  if (key === 'aiTokens') return compact.format(v);
  return whole.format(v);
}

/** Ad spend, per currency: "€40 · $12". */
export function formatSpend(spend: Record<string, number> | undefined): string {
  const entries = Object.entries(spend || {}).filter(([, v]) => v > 0);
  if (!entries.length) return '';
  return entries
    .map(([currency, v]) => {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(v);
      } catch {
        return `${whole.format(v)} ${currency}`;
      }
    })
    .join(' · ');
}

/** The number a feature is judged by, for a vendor. */
export const headline = (feature: FeatureKey, m: Metrics) => m[metaOf(feature).metrics[0]] || 0;
